import {useState, useEffect} from 'react'
import { 
    ERROR_STRING, 
    VALIDATION_ERROR, 
    VALIDATION_TYPE 
} from '../../app/errors'
import { 
    ValidateString, 
    GetProviceCodeFromName 
} from '../../app/utils'
import { ErrorWithString } from '../RegularComponents'
import {
    CanadaProvinceList
} from '../../app/constants'

/*
 * Re-usable input form. We can move this to RegularComponent.js, if it is needed by other screens also.
 * for not its fine here.
 * 
 * props :
 *  - id : The id of the component. This string will be appended by input type.
 *  - onInputChange : The change callback handler. 
 *                    This function will pass the whole object even if just one field is modified.
 *  - disabled : The flag to indicate if the inputs should be disalbed or not. 
 *               The disabled input will not show any value typed in that input box. The state values will 
 *               still be maintained.
 *   - 
 */ 
const AgentContactDetailsInputForm = (props) => {

    const [contactInput,setContactInput] = useState(props.preloadInput)
    const [showDataValidationError, setShowDataValidationError] = useState(props.showValidationError)
    const [firstNameValidationError, setFirstNameValidationError] = useState(VALIDATION_ERROR.NO_ERROR)
    const [lastNameValidationError, setLastNameValidationError] = useState(VALIDATION_ERROR.NO_ERROR)
    const [phoneNumberValidationError, setPhoneNumberValidationError] = useState(VALIDATION_ERROR.NO_ERROR)
    const [emailValidationError, setEmailValidationError] = useState(VALIDATION_ERROR.NO_ERROR)
    const [streetAddressValidationError, setStreetAddressValidationError] = useState(VALIDATION_ERROR.NO_ERROR)
    const [cityValidationError, setCityValidationError] = useState(VALIDATION_ERROR.NO_ERROR)
    const [provinceValidationError, setProvinceValidationError] = useState(VALIDATION_ERROR.NO_ERROR)
    const [postalCodeValidationError, setPostalCodeValidationError] = useState(VALIDATION_ERROR.NO_ERROR)

    useEffect(() => { 
        const validateFirstName = () => { 
            const ret = ValidateString(contactInput.firstName, VALIDATION_TYPE.NAME)
            setFirstNameValidationError(ret)
            return (ret !== VALIDATION_ERROR.NO_ERROR)
        }
    
        const validateLastName = () => { 
            const ret = ValidateString(contactInput.lastName, VALIDATION_TYPE.NAME)
            setLastNameValidationError(ret)
            return (ret !== VALIDATION_ERROR.NO_ERROR)
        }
    
        const validatePhoneNumber = () => { 
            const ret = ValidateString(contactInput.phoneNumber, VALIDATION_TYPE.PHONE_NUMBER)
            setPhoneNumberValidationError(ret)
            return (ret !== VALIDATION_ERROR.NO_ERROR)
        }
    
        const validateEmail = () => { 
            const ret = ValidateString(contactInput.email, VALIDATION_TYPE.EMAIL)
            setEmailValidationError(ret)
            return (ret === VALIDATION_ERROR.NO_ERROR)
        }

        const validateStreetAddress = () => {
            const ret = ValidateString(contactInput.addressData.address1, VALIDATION_TYPE.STRING_NON_EMPTY)
            setStreetAddressValidationError(ret)
            return (ret === VALIDATION_ERROR.NO_ERROR)
        }

        const validateCity = () => {
            const ret = ValidateString(contactInput.addressData.cityName, VALIDATION_TYPE.STRING_NON_EMPTY)
            setCityValidationError(ret)
            return (ret === VALIDATION_ERROR.NO_ERROR)
        }

        const validateProvince = () => {
            let ret = VALIDATION_ERROR.NO_ERROR

            if(!contactInput.addressData.stateName) { 
                ret = VALIDATION_ERROR.EMPTY
            } else { 
                ret = ValidateString(GetProviceCodeFromName(contactInput.addressData.stateName), VALIDATION_TYPE.CANADA_PROVINCE_CODE)
            }

            setProvinceValidationError(ret)
            return (ret === VALIDATION_ERROR.NO_ERROR)
        }

        const validatePostalCode = () => {
            const ret = ValidateString(contactInput.addressData.postalCode, VALIDATION_TYPE.CANADA_POSTAL_CODE)
            setPostalCodeValidationError(ret)
            return (ret === VALIDATION_ERROR.NO_ERROR)
        }

        setContactInput(props.preloadInput)
        setShowDataValidationError(props.showValidationError)

        if(props.showValidationError) { 
            validateFirstName()
            validateLastName()
            validatePhoneNumber()
            validateEmail()
            validateStreetAddress()
            validateCity()
            validateProvince()
            validatePostalCode()
        }

    }, [
        setContactInput,
        setShowDataValidationError, 
        props.showValidationError,
        props.preloadInput,
        contactInput.firstName,
        contactInput.lastName,
        contactInput.phoneNumber,
        contactInput.email,
        contactInput.addressData.address1,
        contactInput.addressData.cityName,
        contactInput.addressData.stateName,
        contactInput.addressData.postalCode
    ])

    const isFirstNameValid = () => { 
        if(showDataValidationError)
            return (firstNameValidationError === VALIDATION_ERROR.NO_ERROR)

        return true
    }

    const isLastNameValid = () => { 
        if (showDataValidationError)
            return (lastNameValidationError === VALIDATION_ERROR.NO_ERROR)
        return true
    }

    const isPhoneNumberValid = () => { 
        if(showDataValidationError)
            return (phoneNumberValidationError === VALIDATION_ERROR.NO_ERROR)
        return true
    }

    const isEmailValid = () => { 
         if(showDataValidationError)
            return(emailValidationError === VALIDATION_ERROR.NO_ERROR)
        return true 
    }

    const isAddressValid = () => { 
        if(showDataValidationError)
            return (streetAddressValidationError === VALIDATION_ERROR.NO_ERROR)
        
        return true
    }

    const isCityValid = () => { 
        if(showDataValidationError)
            return (cityValidationError === VALIDATION_ERROR.NO_ERROR)
    
        return true
    }

    const isProvinceValid = () => { 
        if(showDataValidationError)
            return (provinceValidationError === VALIDATION_ERROR.NO_ERROR)
        
        return true
    }

    const isPostalCodeValid = () => { 
        if(showDataValidationError)
            return (postalCodeValidationError === VALIDATION_ERROR.NO_ERROR)
        
        return true
    }

    const postalCodeErrorDisplayString = () => { 
        if(postalCodeValidationError === VALIDATION_ERROR.CANADA_POSTAL_CODE_INVALID) { 
            return ERROR_STRING.CONTACT_DETAILS_POSTAL_CODE_INVALID
        } else if(postalCodeValidationError === VALIDATION_ERROR.EMPTY) { 
            return ERROR_STRING.CONTACT_DETAILS_POSTAL_CODE_EMPTY
        } else if(postalCodeValidationError !== VALIDATION_ERROR.NO_ERROR) {
            return ERROR_STRING.CONTACT_DETAILS_POSTAL_CODE_EMPTY
        }

        return ""
    }

    const phoneNumberErrorString = () => { 
        switch(phoneNumberValidationError) { 
            case VALIDATION_ERROR.PHONE_NUMBER_INVALID : 
                return ERROR_STRING.CONTACT_DETAILS_PHONE_INVALID
            case VALIDATION_ERROR.EMPTY : 
            default :
                return ERROR_STRING.CONTACT_DETAILS_PHONE_EMPTY
        }
    }

    const emailErrorString = () => {
        switch(emailValidationError) { 
            case VALIDATION_ERROR.EMAIL_INVALID : 
                return ERROR_STRING.CONTACT_DETAILS_EMAIL_INVALID
            case VALIDATION_ERROR.EMPTY :
            default : 
                return ERROR_STRING.CONTACT_DETAILS_EMAIL_EMPTY 
        }
    }

    const onInputChange = (e) => {
        let update = {}
        switch (e.target.id) {
            case props.id + "-first-name":
                update = {firstName : e.target.value}
                break;
            case props.id + '-last-name' :
                update =  {lastName : e.target.value}
                break;
            case props.id + '-phone' :
                update = {phoneNumber : e.target.value}
                break;
            case props.id + '-email' : 
                update = {email : e.target.value}
                break;
            case props.id + '-street-address' : 
                update = {addressData : Object.assign({}, contactInput.addressData, {address1 : e.target.value})}
                break;
            case props.id + '-city' : 
                update = {addressData : Object.assign({}, contactInput.addressData, {cityName : e.target.value})}
                break;  
            case props.id + '-postal-code' : 
                update = {addressData : Object.assign({}, contactInput.addressData, {postalCode : e.target.value})}
                break;                      
            default:
                break;
        }
        setContactInput(Object.assign({}, contactInput,update))
        props.onInputChange(update)
    }
    
    const provinceOptionElements = []
    for (const key in CanadaProvinceList) {
        if (Object.hasOwnProperty.call(CanadaProvinceList, key)) {
            const element = CanadaProvinceList[key];
            provinceOptionElements.push(<option key={key} value={key}>{element}</option>)
        }
    }

    return (
        <div 
            className="ns-fieldset" 
            id={props.id + "-form"}
        >
            <div className={"ns-fieldset" + (!isFirstNameValid() ? " has-error ns-fieldset-message-error" : "")}>
                <label 
                    className="ns-label"
                    id={props.id + "-first-name-label"} 
                    htmlFor={props.id + "-first-name"}>
                        {"First name"}
                </label>
                {!isFirstNameValid() && 
                <span id={props.id + "-first-name-error"} className="ns-error-message field-validation-error">
                    {ErrorWithString(ERROR_STRING.CONTACT_DETAILS_FIRST_NAME_EMPTY)}
                </span>}
                <input 
                    className={"fw-20" + (isFirstNameValid() ? " ns-input" : " ns-input--error")}
                    disabled={props.disabled}
                    type="text" 
                    autoComplete="given-name"
                    id={props.id + "-first-name"}
                    name={props.id + "-first-name"} 
                    onInput={onInputChange}
                    value={props.disabled ? "" : contactInput.firstName} />
            </div>
            <div className={"ns-fieldset" + (!isLastNameValid() ? " has-error ns-fieldset-message-error" : "")}>
                <label
                    className="ns-label" 
                    id={props.id + "-last-name-label"} 
                    htmlFor={props.id + "-last-name"}>
                        {"Last name"}
                </label>
                {!isLastNameValid() && 
                    <span id={props.id + "-last-name-error"} class="ns-error-message field-validation-error">
                        {ErrorWithString(ERROR_STRING.CONTACT_DETAILS_LAST_NAME_EMPTY)}
                    </span>}
                <input 
                    className={"fw-20" + (isLastNameValid() ? " ns-input" : " ns-input--error")}
                    disabled={props.disabled}
                    type="text" 
                    autoComplete="family-name"
                    id={props.id + "-last-name"} 
                    name={props.id + "-last-name"} 
                    onInput={onInputChange}
                    value={props.disabled ? "" : contactInput.lastName} />
                
            </div>
            <div className={"ns-fieldset" + (!isEmailValid() ? " has-error ns-fieldset-message-error" : "")}>
                <label
                    className="ns-label"
                    id={props.id + "-email-label"} 
                    htmlFor={props.id + "-email"}>
                        {"Email"}
                </label>
                {!isEmailValid() && 
                    <span id={props.id + "-email-error"} className="ns-error-message field-validation-error">
                        {ErrorWithString(emailErrorString())}
                    </span>}
                <input 
                    className={"fw-20" + (isEmailValid() ? " ns-input" : " ns-input--error")}
                    type="text"
                    disabled={props.disabled}
                    autoComplete="email" 
                    id={props.id + "-email"} 
                    name={props.id + "-email"} 
                    onInput={onInputChange}
                    value={props.disabled ? "" : contactInput.email} />
            </div>
            <div className={"ns-fieldset" + (!isAddressValid() ? " has-error ns-fieldset-message-error" : "")}>
                <label
                    className="ns-label"
                    id={props.id + "-street-address-label"} 
                    htmlFor={props.id + "-street-address"}>
                        {"Street address"}
                </label>
                {!isAddressValid() && <span id={props.id + "-street-address-error"} 
                    className="ns-error-message field-validation-error">
                    {ErrorWithString(ERROR_STRING.CONTACT_DETAILS_STREET_ADDRESS_EMPTY)}
                </span>}
                <input 
                    className={"fw-25" + (isAddressValid() ? " ns-input" : " ns-input--error")}
                    type="text"
                    disabled={props.disabled}
                    autoComplete="street-address" 
                    id={props.id + "-street-address"} 
                    name={props.id + "-street-address"} 
                    onInput={onInputChange}
                    value={props.disabled ? "" : contactInput.addressData.address1} />
                
            </div>
            <div className={"ns-fieldset" + (!isCityValid() ? " has-error ns-fieldset-message-error" : "")}>
                <label
                    className="ns-label"
                    id={props.id + "-city-label"} 
                    htmlFor={props.id + "-city"}>
                        {"City/Town"}
                </label>
                {!isCityValid() && <span id={props.id + "-city-error"} className="ns-error-message fieldset-validation-error">
                    {ErrorWithString(ERROR_STRING.CONTACT_DETAILS_CITY_EMPTY)}
                </span>}
                <input 
                    className={"fw-25" + (isCityValid() ? " ns-input" : " ns-input--error")}
                    type="text"
                    disabled={props.disabled}
                    autoComplete="address-level2" 
                    id={props.id + "-city"} 
                    name={props.id + "-city"} 
                    onInput={onInputChange}
                    value={props.disabled ? "" : contactInput.addressData.cityName} />
                
            </div>
            <div className={"ns-fieldset" + (!isProvinceValid() ? " has-error ns-fieldset-message-error" : "")}>
                <label 
                    id={props.id + "-province-select-label"}
                    className="ns-label"
                    htmlFor={props.id + "-province-select-input"}>
                        Province
                </label>
                {!isProvinceValid() && 
                <span id={props.id + "-province-select-input-error"} className="ns-error-message fieldset-validation-error">
                    {ErrorWithString(ERROR_STRING.CONTACT_DETAILS_PROVINCE_EMPTY)}
                </span>}
                <div className="select fw-25 inlineDisplay">
                    <select 
                        className={!isProvinceValid() ? "select--error" : ""}
                        style={{width : "25em"}}
                        id={props.id + "-province-select-input"}
                        name={props.id + "-province-select-input"}
                        value={contactInput.addressData.stateName ? GetProviceCodeFromName(contactInput.addressData.stateName) : ""}
                        onChange={(e) => {
                                setProvinceValidationError(VALIDATION_ERROR.NO_ERROR)
                                let updateObj = Object.assign({}, contactInput,{
                                    addressData : Object.assign({}, contactInput.addressData, 
                                        {
                                            stateName : CanadaProvinceList[e.target.value]
                                        })
                                })
                                setContactInput(updateObj)
                                props.onInputChange(updateObj)
                            }}>
                            {provinceOptionElements}
                    </select>
                </div>
                
            </div>
            <div className={"ns-fieldset" + (!isPostalCodeValid() ? " has-error ns-fieldset-message-error" : "")}>
                <label
                    className="ns-label"
                    id={props.id + "-postal-code-label"} 
                    htmlFor={props.id + "-postal-code"}>
                        {"Postal code"}
                </label>
                {!isPostalCodeValid() && 
                <span id={props.id + "-postal-code-error"} className="ns-error-message">
                    {ErrorWithString(postalCodeErrorDisplayString())}
                </span>}
                <input 
                    className={"fw-25" + (isPostalCodeValid() ? " ns-input" : " ns-input--error")}
                    type="text"
                    disabled={props.disabled}
                    autoComplete="postal-code" 
                    id={props.id + "-postal-code"} 
                    name={props.id + "-postal-code"}
                    onInput={onInputChange}
                    value={props.disabled ? "" : contactInput.addressData.postalCode} />
                
            </div>
            <div className={"ns-fieldset" + (!isPhoneNumberValid() ? " has-error ns-fieldset-message-error" : "")}>
                    <label
                        className="ns-label" 
                        id={ props.id + "-phone-label"} 
                        htmlFor={props.id + "-phone"}>
                            {"Phone number"}
                    </label>
                    {!isPhoneNumberValid() && 
                        <span id={props.id + "-phone-error"} 
                        className="ns-error-message fielset-validation-error">
                            {ErrorWithString(phoneNumberErrorString())}
                        </span>}
                    <input 
                        className={"fw-20" + (isPhoneNumberValid() ? " ns-input" : " ns-input--error")}
                        type="text"
                        disabled={props.disabled} 
                        id={props.id + "-phone"}
                        name={props.id + "-phone"}
                        onInput={onInputChange} 
                        autoComplete="tel"
                        value={props.disabled ? "" : contactInput.phoneNumber} />
                    
                </div>
        </div>
    )
}

export default AgentContactDetailsInputForm
